import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  LinkIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { exportAPI } from '../services/api';
import { API_BASE_URL } from '../config/api';

// Custom Pill icon
const MedicationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611l-3.773.628a1.5 1.5 0 0 1-1.504-.627l-.628-1.047a1.5 1.5 0 0 0-1.504-.628l-3.773.629c-1.718.286-2.299-2.379-1.067-3.611L5 14.5" />
  </svg>
);

interface SideEffect {
  name: string;
  frequency: string;
  severity: string;
}

interface AdditionalReading {
  title: string;
  url: string;
  source: string;
}

interface MedicationData {
  generic_name: string;
  brand_names: string[];
  drug_class: string;
  mechanism: string;
  common_uses: string[];
  dosing_info: string;
  side_effects: SideEffect[];
  warnings: string[];
  storage: string;
  cost_info?: string;
  generic_alternatives: string[];
  food_beverage_interactions?: string[];
  additional_reading: AdditionalReading[];
}

interface Interaction {
  drug_pair?: string;
  severity: string;
  description: string;
  recommendation: string;
}

interface FoodInteraction {
  drug: string;
  food: string;
  severity: string;
  description: string;
  recommendation: string;
}

interface InteractionResult {
  interactions: Interaction[];
  food_interactions: FoodInteraction[];
  condition_interactions: any[];
  summary: string;
  risk_level: string;
  recommendations: string[];
  when_to_seek_help: string[];
}

interface UserMedication {
  id: string;
  name: string;
  dosage?: string;
  frequency?: string;
  prescriber?: string;
  pharmacy?: string;
  start_date?: string;
  notes?: string;
  is_active: boolean;
  reminders_enabled: boolean;
  created_at: string;
}

type TabType = 'search' | 'interactions' | 'my-list';

// Helper to determine tab from URL path
const getTabFromPath = (pathname: string): TabType => {
  if (pathname === '/interaction-checker') return 'interactions';
  if (pathname === '/my-medications') return 'my-list';
  return 'search'; // /medication-info defaults to search
};

export const MedicationInfo: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Tab state - initialize based on URL path
  const [activeTab, setActiveTab] = useState<TabType>(() => getTabFromPath(location.pathname));

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState<MedicationData | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Interaction checker state
  const [interactionMeds, setInteractionMeds] = useState<string[]>(['', '']);
  const [interactionResult, setInteractionResult] = useState<InteractionResult | null>(null);
  const [interactionLoading, setInteractionLoading] = useState(false);
  const [interactionError, setInteractionError] = useState<string | null>(null);

  // My medications state
  const [myMedications, setMyMedications] = useState<UserMedication[]>([]);
  const [myMedsLoading, setMyMedsLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMed, setEditingMed] = useState<UserMedication | null>(null);

  // Add/Edit form state
  const [formData, setFormData] = useState({
    name: '',
    dosage: '',
    frequency: '',
    prescriber: '',
    pharmacy: '',
    start_date: '',
    notes: '',
    reminders_enabled: false,
  });


  // Sync tab with URL path when navigating
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  // Load my medications on tab change
  useEffect(() => {
    if (activeTab === 'my-list') {
      fetchMyMedications();
    }
  }, [activeTab]);

  const fetchMyMedications = async () => {
    setMyMedsLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setMyMedications([]);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/medications/my-list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMyMedications(data.medications || []);
      }
    } catch (error) {
      console.error('Failed to fetch medications:', error);
    } finally {
      setMyMedsLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchError(t('medicationInfo:errors.nameRequired'));
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setSearchResult(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/medications/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ query: searchQuery }),
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setSearchResult(data.medication);
    } catch (error) {
      console.error('Search error:', error);
      setSearchError(t('medicationInfo:errors.searchFailed'));
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCheckInteractions = async (e: React.FormEvent) => {
    e.preventDefault();
    const validMeds = interactionMeds.filter((m) => m.trim());
    if (validMeds.length < 2) {
      setInteractionError(t('medicationInfo:errors.minTwoMeds'));
      return;
    }

    setInteractionLoading(true);
    setInteractionError(null);
    setInteractionResult(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/medications/check-interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          medications: validMeds,
          include_food: true,
          include_conditions: true,
        }),
      });

      if (!response.ok) {
        throw new Error('Interaction check failed');
      }

      const data = await response.json();
      setInteractionResult(data.result);
    } catch (error) {
      console.error('Interaction check error:', error);
      setInteractionError(t('medicationInfo:errors.interactionFailed'));
    } finally {
      setInteractionLoading(false);
    }
  };

  const addInteractionMed = () => {
    setInteractionMeds([...interactionMeds, '']);
  };

  const removeInteractionMed = (index: number) => {
    if (interactionMeds.length > 2) {
      setInteractionMeds(interactionMeds.filter((_, i) => i !== index));
    }
  };

  const updateInteractionMed = (index: number, value: string) => {
    const updated = [...interactionMeds];
    updated[index] = value;
    setInteractionMeds(updated);
  };

  const handleAddMedication = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert(t('medicationInfo:errors.loginRequired'));
        return;
      }

      const response = await fetch(`${API_BASE_URL}/medications/my-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowAddForm(false);
        setFormData({
          name: '',
          dosage: '',
          frequency: '',
          prescriber: '',
          pharmacy: '',
          start_date: '',
          notes: '',
          reminders_enabled: false,
        });
        fetchMyMedications();
      }
    } catch (error) {
      console.error('Add medication error:', error);
    }
  };

  const handleDeleteMedication = async (id: string) => {
    if (!window.confirm(t('medicationInfo:confirm.delete'))) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/medications/my-list/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchMyMedications();
      }
    } catch (error) {
      console.error('Delete medication error:', error);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'major':
      case 'serious':
      case 'high':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
      case 'moderate':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
      case 'minor':
      case 'mild':
      case 'low':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/30';
    }
  };

  const getFrequencyBadge = (frequency: string) => {
    switch (frequency.toLowerCase()) {
      case 'common':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'less_common':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'rare':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  // Generate medication list content for export
  const generateMedicationListContent = () => {
    const date = new Date().toLocaleDateString();
    let content = `MY MEDICATION LIST\nGenerated: ${date}\n\n`;
    content += `Total Medications: ${myMedications.length}\n\n`;
    content += '─'.repeat(50) + '\n\n';

    myMedications.forEach((med, index) => {
      content += `${index + 1}. ${med.name}\n`;
      if (med.dosage) content += `   Dosage: ${med.dosage}\n`;
      if (med.frequency) content += `   Frequency: ${med.frequency}\n`;
      if (med.prescriber) content += `   Prescriber: ${med.prescriber}\n`;
      if (med.pharmacy) content += `   Pharmacy: ${med.pharmacy}\n`;
      if (med.start_date) content += `   Start Date: ${med.start_date}\n`;
      if (med.notes) content += `   Notes: ${med.notes}\n`;
      content += `   Status: ${med.is_active ? 'Active' : 'Inactive'}\n`;
      content += '\n';
    });

    content += '─'.repeat(50) + '\n';
    content += '\nThis list is for informational purposes only.\n';
    content += 'Always consult your healthcare provider before making changes to your medications.\n';

    return content;
  };

  const printMedicationList = () => {
    if (myMedications.length === 0) return;

    const date = new Date().toLocaleDateString();
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>My Medication List - ${date}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; margin-bottom: 10px; color: #1f2937; }
            h2 { font-size: 14px; color: #6b7280; margin-bottom: 20px; }
            .medication {
              margin-bottom: 20px;
              padding: 15px;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              page-break-inside: avoid;
            }
            .med-name { font-size: 18px; font-weight: 600; color: #1f2937; margin-bottom: 8px; }
            .med-detail { font-size: 14px; color: #4b5563; margin: 4px 0; }
            .med-detail strong { color: #374151; }
            .status-active { color: #059669; }
            .status-inactive { color: #9ca3af; }
            .footer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 1px solid #e5e7eb;
              font-size: 12px;
              color: #6b7280;
            }
            @media print {
              body { padding: 20px; }
              .medication { border: 1px solid #ccc; }
            }
          </style>
        </head>
        <body>
          <h1>My Medication List</h1>
          <h2>Generated: ${date} | Total: ${myMedications.length} medication(s)</h2>
          ${myMedications.map((med) => `
            <div class="medication">
              <div class="med-name">${med.name}</div>
              ${med.dosage ? `<div class="med-detail"><strong>Dosage:</strong> ${med.dosage}</div>` : ''}
              ${med.frequency ? `<div class="med-detail"><strong>Frequency:</strong> ${med.frequency}</div>` : ''}
              ${med.prescriber ? `<div class="med-detail"><strong>Prescriber:</strong> ${med.prescriber}</div>` : ''}
              ${med.pharmacy ? `<div class="med-detail"><strong>Pharmacy:</strong> ${med.pharmacy}</div>` : ''}
              ${med.start_date ? `<div class="med-detail"><strong>Start Date:</strong> ${med.start_date}</div>` : ''}
              ${med.notes ? `<div class="med-detail"><strong>Notes:</strong> ${med.notes}</div>` : ''}
              <div class="med-detail"><strong>Status:</strong> <span class="${med.is_active ? 'status-active' : 'status-inactive'}">${med.is_active ? 'Active' : 'Inactive'}</span></div>
            </div>
          `).join('')}
          <div class="footer">
            <p>This list is for informational purposes only.</p>
            <p>Always consult your healthcare provider before making changes to your medications.</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const exportMedicationList = async (format: 'word' | 'pdf' | 'text') => {
    if (myMedications.length === 0) return;

    const content = generateMedicationListContent();
    const date = new Date().toLocaleDateString().replace(/\//g, '-');
    const extensionMap: Record<string, string> = { word: 'docx', pdf: 'pdf', text: 'txt' };
    const extension = extensionMap[format];
    const filename = `my-medication-list-${date}.${extension}`;

    try {
      const blob = await exportAPI.exportDocument({
        content,
        title: `My Medication List - ${date}`,
        format: extension as 'docx' | 'pdf' | 'txt'
      });

      // Try to use File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const mimeTypes: Record<string, string> = {
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            pdf: 'application/pdf',
            txt: 'text/plain'
          };

          const descriptions: Record<string, string> = {
            word: 'Word Document',
            pdf: 'PDF Document',
            text: 'Text File'
          };

          const handle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: descriptions[format],
              accept: { [mimeTypes[extension]]: [`.${extension}`] }
            }]
          });

          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch (err: any) {
          if (err.name === 'AbortError') {
            return;
          }
          console.warn('File System Access API failed, falling back to standard download:', err);
        }
      }

      // Fallback to standard download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    await exportMedicationList(format);
  };

  return (
    <div className="flex flex-col h-full p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <MedicationIcon className="h-12 w-12 text-blue-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('medicationInfo:title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('medicationInfo:subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('search')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'search'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <MagnifyingGlassIcon className="h-4 w-4 inline mr-2" />
            {t('medicationInfo:tabs.search')}
          </button>
          <button
            onClick={() => setActiveTab('interactions')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'interactions'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <ExclamationTriangleIcon className="h-4 w-4 inline mr-2" />
            {t('medicationInfo:tabs.interactions')}
          </button>
          <button
            onClick={() => setActiveTab('my-list')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'my-list'
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <ListBulletIcon className="h-4 w-4 inline mr-2" />
            {t('medicationInfo:tabs.myList')}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 max-w-4xl mx-auto w-full overflow-y-auto">
        {/* Search Tab */}
        {activeTab === 'search' && (
          <div className="space-y-6">
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('medicationInfo:search.placeholder')}
                className="flex-1 px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 placeholder-gray-500 dark:placeholder-gray-400"
                disabled={searchLoading}
              />
              <button
                type="submit"
                disabled={searchLoading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
              >
                {searchLoading ? '...' : t('medicationInfo:search.button')}
              </button>
            </form>

            {searchError && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-600 dark:text-red-400">{searchError}</p>
              </div>
            )}

            {searchResult && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
                {/* Header */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {searchResult.generic_name}
                  </h2>
                  {searchResult.brand_names.length > 0 && (
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                      {t('medicationInfo:results.brandNames')}: {searchResult.brand_names.join(', ')}
                    </p>
                  )}
                  <span className="inline-block mt-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm">
                    {searchResult.drug_class}
                  </span>
                </div>

                {/* Mechanism */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {t('medicationInfo:results.howItWorks')}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">{searchResult.mechanism}</p>
                </div>

                {/* Common Uses */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {t('medicationInfo:results.commonUses')}
                  </h3>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                    {searchResult.common_uses.map((use, i) => (
                      <li key={i}>{use}</li>
                    ))}
                  </ul>
                </div>

                {/* Dosing Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {t('medicationInfo:results.dosing')}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">{searchResult.dosing_info}</p>
                </div>

                {/* Side Effects */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {t('medicationInfo:results.sideEffects')}
                  </h3>
                  <div className="grid gap-2">
                    {searchResult.side_effects.map((effect, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
                      >
                        <span className="text-gray-700 dark:text-gray-300">{effect.name}</span>
                        <div className="flex gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${getFrequencyBadge(effect.frequency)}`}>
                            {effect.frequency}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(effect.severity)}`}>
                            {effect.severity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Warnings */}
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2 flex items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                    {t('medicationInfo:results.warnings')}
                  </h3>
                  <ul className="list-disc list-inside text-red-700 dark:text-red-300 space-y-1">
                    {searchResult.warnings.map((warning, i) => (
                      <li key={i}>{warning}</li>
                    ))}
                  </ul>
                </div>

                {/* Food Interactions */}
                {searchResult.food_beverage_interactions && searchResult.food_beverage_interactions.length > 0 && (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <h3 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
                      {t('medicationInfo:results.foodInteractions')}
                    </h3>
                    <ul className="list-disc list-inside text-yellow-700 dark:text-yellow-300 space-y-1">
                      {searchResult.food_beverage_interactions.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Storage */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {t('medicationInfo:results.storage')}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">{searchResult.storage}</p>
                </div>

                {/* Cost Info */}
                {searchResult.cost_info && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('medicationInfo:results.costInfo')}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300">{searchResult.cost_info}</p>
                  </div>
                )}

                {/* Generic Alternatives */}
                {searchResult.generic_alternatives.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('medicationInfo:results.genericAlternatives')}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {searchResult.generic_alternatives.map((alt, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm"
                        >
                          {alt}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Reading */}
                {searchResult.additional_reading && searchResult.additional_reading.length > 0 && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <h3 className="font-semibold text-purple-800 dark:text-purple-200 mb-2 flex items-center">
                      <LinkIcon className="h-5 w-5 mr-2" />
                      {t('medicationInfo:results.additionalReading')}
                    </h3>
                    <ul className="space-y-2">
                      {searchResult.additional_reading.map((link, i) => (
                        <li key={i}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-700 dark:text-purple-300 hover:underline"
                          >
                            {link.title}
                          </a>
                          <span className="text-purple-600 dark:text-purple-400 ml-2">
                            - {link.source}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Interactions Tab */}
        {activeTab === 'interactions' && (
          <div className="space-y-6">
            <form onSubmit={handleCheckInteractions} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('medicationInfo:interactions.title')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('medicationInfo:interactions.description')}
              </p>

              <div className="space-y-3">
                {interactionMeds.map((med, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={med}
                      onChange={(e) => updateInteractionMed(index, e.target.value)}
                      placeholder={`${t('medicationInfo:interactions.medication')} ${index + 1}`}
                      className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 placeholder-gray-500 dark:placeholder-gray-400"
                    />
                    {interactionMeds.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeInteractionMed(index)}
                        className="p-2 text-red-600 hover:text-red-800 dark:text-red-400"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={addInteractionMed}
                  className="px-4 py-2 text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20"
                >
                  <PlusIcon className="h-4 w-4 inline mr-1" />
                  {t('medicationInfo:interactions.addMore')}
                </button>
                <button
                  type="submit"
                  disabled={interactionLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {interactionLoading ? '...' : t('medicationInfo:interactions.check')}
                </button>
              </div>
            </form>

            {interactionError && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-red-600 dark:text-red-400">{interactionError}</p>
              </div>
            )}

            {interactionResult && (
              <div className="space-y-4">
                {/* Risk Level Summary */}
                <div className={`p-4 rounded-lg border ${getSeverityColor(interactionResult.risk_level)}`}>
                  <h3 className="font-semibold mb-2">
                    {t('medicationInfo:interactions.riskLevel')}: {interactionResult.risk_level.toUpperCase()}
                  </h3>
                  <p>{interactionResult.summary}</p>
                </div>

                {/* Drug-Drug Interactions */}
                {interactionResult.interactions.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      {t('medicationInfo:interactions.drugDrug')}
                    </h3>
                    <div className="space-y-3">
                      {interactionResult.interactions.map((interaction, i) => (
                        <div
                          key={i}
                          className={`p-4 rounded-lg border ${getSeverityColor(interaction.severity)}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{interaction.drug_pair}</span>
                            <span className={`px-2 py-0.5 rounded text-xs uppercase ${getSeverityColor(interaction.severity)}`}>
                              {interaction.severity}
                            </span>
                          </div>
                          <p className="text-sm mb-2">{interaction.description}</p>
                          <p className="text-sm font-medium">
                            {t('medicationInfo:interactions.recommendation')}: {interaction.recommendation}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Food Interactions */}
                {interactionResult.food_interactions && interactionResult.food_interactions.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      {t('medicationInfo:interactions.foodDrug')}
                    </h3>
                    <div className="space-y-3">
                      {interactionResult.food_interactions.map((interaction, i) => (
                        <div
                          key={i}
                          className={`p-4 rounded-lg border ${getSeverityColor(interaction.severity)}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{interaction.drug} + {interaction.food}</span>
                            <span className={`px-2 py-0.5 rounded text-xs uppercase ${getSeverityColor(interaction.severity)}`}>
                              {interaction.severity}
                            </span>
                          </div>
                          <p className="text-sm mb-2">{interaction.description}</p>
                          <p className="text-sm font-medium">
                            {t('medicationInfo:interactions.recommendation')}: {interaction.recommendation}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {interactionResult.recommendations && interactionResult.recommendations.length > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">
                      {t('medicationInfo:interactions.recommendations')}
                    </h3>
                    <ul className="list-disc list-inside text-green-700 dark:text-green-300 space-y-1">
                      {interactionResult.recommendations.map((rec, i) => (
                        <li key={i}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* When to Seek Help */}
                {interactionResult.when_to_seek_help && interactionResult.when_to_seek_help.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <h3 className="font-semibold text-red-800 dark:text-red-200 mb-2 flex items-center">
                      <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                      {t('medicationInfo:interactions.whenToSeekHelp')}
                    </h3>
                    <ul className="list-disc list-inside text-red-700 dark:text-red-300 space-y-1">
                      {interactionResult.when_to_seek_help.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* My Medications Tab */}
        {activeTab === 'my-list' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {t('medicationInfo:myList.title')}
              </h3>
              <div className="flex items-center gap-2">
                {/* Export/Print dropdown - only show when there are medications */}
                {myMedications.length > 0 && (
                  <ExportDropdownMenu
                    onExport={handleExport}
                    onPrint={printMedicationList}
                    showPrint={true}
                  />
                )}
                <button
                  onClick={() => setShowAddForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4 inline mr-1" />
                  {t('medicationInfo:myList.add')}
                </button>
              </div>
            </div>

            {/* Add Form Modal */}
            {showAddForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    {t('medicationInfo:myList.addMedication')}
                  </h3>
                  <form onSubmit={handleAddMedication} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('medicationInfo:myList.form.name')} *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('medicationInfo:myList.form.dosage')}
                        </label>
                        <input
                          type="text"
                          value={formData.dosage}
                          onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                          placeholder="e.g., 10mg"
                          className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('medicationInfo:myList.form.frequency')}
                        </label>
                        <input
                          type="text"
                          value={formData.frequency}
                          onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                          placeholder="e.g., Twice daily"
                          className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('medicationInfo:myList.form.prescriber')}
                      </label>
                      <input
                        type="text"
                        value={formData.prescriber}
                        onChange={(e) => setFormData({ ...formData, prescriber: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('medicationInfo:myList.form.pharmacy')}
                      </label>
                      <input
                        type="text"
                        value={formData.pharmacy}
                        onChange={(e) => setFormData({ ...formData, pharmacy: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('medicationInfo:myList.form.startDate')}
                      </label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg dark:[color-scheme:dark]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('medicationInfo:myList.form.notes')}
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                      >
                        {t('common:buttons.cancel')}
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        {t('common:buttons.save')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Medications List */}
            {myMedsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : myMedications.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <MedicationIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {t('medicationInfo:myList.noMedications')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                  {t('medicationInfo:myList.addFirst')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {myMedications.map((med) => (
                  <div
                    key={med.id}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-start justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">{med.name}</h4>
                        {med.is_active ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircleIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 space-y-0.5">
                        {med.dosage && <p>{t('medicationInfo:myList.form.dosage')}: {med.dosage}</p>}
                        {med.frequency && <p>{t('medicationInfo:myList.form.frequency')}: {med.frequency}</p>}
                        {med.prescriber && <p>{t('medicationInfo:myList.form.prescriber')}: {med.prescriber}</p>}
                        {med.pharmacy && <p>{t('medicationInfo:myList.form.pharmacy')}: {med.pharmacy}</p>}
                        {med.notes && <p className="italic">{med.notes}</p>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteMedication(med.id)}
                      className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}

                {/* Check All Interactions Button */}
                {myMedications.length >= 2 && (
                  <button
                    onClick={async () => {
                      const medNames = myMedications.map((m) => m.name);
                      setInteractionMeds(medNames);
                      setActiveTab('interactions');
                      // Trigger the check
                      setTimeout(() => {
                        const form = document.querySelector('form') as HTMLFormElement;
                        if (form) form.requestSubmit();
                      }, 100);
                    }}
                    className="w-full px-4 py-3 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/50 flex items-center justify-center gap-2"
                  >
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    {t('medicationInfo:myList.checkAllInteractions')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Disclaimer Footer */}
      <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg max-w-4xl mx-auto w-full">
        <p className="text-sm text-amber-800 dark:text-amber-200 text-center">
          <strong>{t('medicationInfo:disclaimer.title')}:</strong> {t('medicationInfo:disclaimer.text')}
        </p>
      </div>

      {/* Loading Overlay */}
      {(searchLoading || interactionLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-gray-900 dark:text-gray-100">{t('medicationInfo:loading')}</span>
          </div>
        </div>
      )}

    </div>
  );
};
